/**
 * Ask about this OKR — chat surface for the AI Coach drawer.
 * Transcript scrolls inside its own container (auto-sticks to bottom only when
 * the user is already at the bottom). Input is sticky at the bottom of the drawer.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Send, Trash2, AlertCircle, RotateCw, Sparkles } from 'lucide-react';
import { LoadingSpinner } from '../common';
import { useAICoachChat, type ChatMessage } from '../../hooks/useAICoachChat';
import { PURPLE } from './ai-theme';

interface Props {
  okrId: string;
}

export const AskOKRChat: React.FC<Props> = ({ okrId }) => {
  const { messages, isLoading, ask, retry, isAsking, clear, isClearing, capReached } = useAICoachChat(okrId);
  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);

  // Track whether the user is parked at the bottom so we only auto-scroll when they are.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = fromBottom < 40;
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll on new messages, but only if the user hasn't scrolled up.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, isAsking]);

  const submit = async () => {
    const q = input.trim();
    if (!q || isAsking) return;
    setInput('');
    try { await ask(q); } catch { /* toast handled in hook */ }
  };

  // Anchor "Thinking…" to message shape: only show it while we're actually
  // waiting for the assistant's reply (last message is a user message). Once
  // the first delta lands, an assistant row exists and the blinking cursor on
  // that bubble takes over — so this indicator vanishes the moment streaming
  // starts producing visible text.
  const lastMessage = messages[messages.length - 1];
  const showThinking = isAsking && (!lastMessage || lastMessage.role === 'user');

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  const handleClear = async () => {
    if (!messages.length) return;
    if (!window.confirm('Clear this conversation? Messages are soft-deleted and gone from the UI.')) return;
    try { await clear(); } catch { /* toast handled */ }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 border-t border-stone-200">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-stone-200 text-[10px] font-bold uppercase tracking-wide text-stone-500">
        <span style={{ color: PURPLE }}>Q&amp;A</span>
        <span className="flex-1" />
        {messages.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            disabled={isClearing}
            className="text-stone-400 hover:text-stone-700 flex items-center gap-1 text-[10px] uppercase tracking-wide"
            title="Clear chat"
          >
            <Trash2 className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0">
        {isLoading ? (
          <div className="text-[13px] text-stone-400">Loading…</div>
        ) : messages.length === 0 ? (
          <p className="text-[13px] text-stone-400 italic">
            Ask anything about this OKR — try “What should I focus on this week?”
          </p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.clientId ?? m.id}
              m={m}
              onRetry={() => { void retry(m).catch(() => undefined); }}
              retryDisabled={isAsking || capReached}
            />
          ))
        )}
        {showThinking && <ThinkingBubble />}
      </div>

      <div className="border-t border-stone-200 p-2">
        {capReached && <CapReachedBanner />}
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={capReached ? 'Daily AI limit reached — try again after 00:00 UTC' : 'Ask about this OKR…'}
            rows={1}
            maxLength={1000}
            disabled={capReached}
            className="flex-1 resize-none px-2 py-1.5 text-[14px] border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 max-h-24 disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!input.trim() || isAsking || capReached}
            aria-label="Send"
            className="shrink-0 p-2 rounded-lg text-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: PURPLE }}
          >
            {isAsking ? <LoadingSpinner size="sm" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[10px] text-stone-400 mt-1">Enter to send · Shift+Enter for newline</p>
      </div>
    </div>
  );
};

interface MessageBubbleProps {
  m: ChatMessage;
  onRetry?: () => void;
  retryDisabled?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ m, onRetry, retryDisabled }) => {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className={`max-w-[85%] rounded-xl px-2.5 py-1.5 text-[13px] leading-snug whitespace-pre-wrap ${
          m.status === 'failed'
            ? 'bg-red-50 text-red-800 border border-red-200'
            : 'bg-stone-100 text-stone-800'
        }`}>
          {m.content}
          {m.status === 'failed' && (
            <div className="mt-1 text-[10px] flex items-center justify-between gap-2">
              <span className="flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Failed to send
              </span>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  disabled={retryDisabled}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-red-700 hover:bg-red-100 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed font-medium"
                >
                  <RotateCw className="w-3 h-3" /> Retry
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="text-[13px] leading-snug text-stone-700 whitespace-pre-wrap">
      {m.content}
      {m.isStreaming && (
        <span
          aria-hidden="true"
          className="inline-block ml-0.5 animate-pulse"
          style={{ color: PURPLE }}
        >
          ▍
        </span>
      )}
    </div>
  );
};

const CapReachedBanner: React.FC = () => (
  <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5">
    <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
    <div className="text-[12px] leading-snug text-amber-800">
      <div className="font-semibold">Daily AI limit reached</div>
      <div className="text-amber-700">
        Your organization has hit today's AI token cap. Chat resumes at 00:00 UTC.
      </div>
    </div>
  </div>
);

const ThinkingBubble: React.FC = () => (
  <div className="text-[13px] italic flex items-center gap-1.5" style={{ color: PURPLE }}>
    <Sparkles className="w-3 h-3 animate-pulse" />
    <span className="inline-flex gap-0.5 mr-0.5">
      <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: PURPLE, animationDelay: '0ms' }} />
      <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: PURPLE, animationDelay: '120ms' }} />
      <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: PURPLE, animationDelay: '240ms' }} />
    </span>
    AI is thinking…
  </div>
);
